// animalAddendumAccommodation — the Send page's "RRA or pending RRA" block (WEL-182), as pure logic.
//
// Staff can mark one or more of the animals on an Animal Addendum "RRA or pending RRA". Those animals
// are $0 on the household's form and on the addendum. RRA = approved (no added charge); Pending RRA =
// $0 for now, and the addendum's neutral clause states the "if not approved" rent and deposit.
//
// Nothing here creates or links an RRA case (Miles: "No case link") — the page does not track the
// decision, so the staff helper says to send the written notice and set up the charges in AppFolio
// by hand if a pending request is denied.
//
// Staff-facing only: this module may say "RRA". The household form, emails and addendum never name an
// accommodation (Rule H) — their wording lives in animalAddendumForm.pendingAnimalLine and the API.
//
// No React, no network, no `@/` imports, so it runs under a bare `npx tsx --test`:
//
//   cd apps/web && NODE_ENV=test npx tsx --experimental-test-module-mocks --test src/lib/animalAddendumAccommodation.test.ts
//
// Ids: a row's `key` is a local React key. It never reaches the wire (the API mints each animal's id)
// and is never rendered.

import { formatMoneyCents } from './money';
import {
  ANIMAL_KIND_CHOICES,
  animalTypeForChoice,
  asksAnimalField,
  type AnimalKindChoice,
} from './animalQuestions';
import type { AnimalAddendumAccommodationAnimalInput } from './animalAddendumClient';

/**
 * Who sees the block: logins on the RRA staff roster. Marking an animal RRA or pending RRA zeroes its
 * rent and deposit — the same power as the lease builder's waiver tick — so the API refuses it (403)
 * for anyone off the roster, and the page does not offer it to them. GET /api/me's
 * `capabilities.accommodations` is that roster; it is read once at sign-in (AuthContext), so this
 * costs nothing. Null (not answered yet) hides the block; a failed /api/me read opens it, as it does
 * for every capability, and the API's refusal is the gate that holds. Structural type so this
 * module stays free of `@/` imports.
 */
export function mayMarkAccommodationAnimals(
  capabilities: { accommodations?: boolean } | null | undefined,
): boolean {
  return capabilities?.accommodations === true;
}

/** The API accepts at most this many (InitiateAnimalAddendumBody.accommodationAnimals). */
export const MAX_ACCOMMODATION_ANIMALS = 10;
/** The API takes 1–80 characters of name. */
export const MAX_ACCOMMODATION_NAME = 80;
/** Breed is free text; capped so a paste cannot run away. */
export const MAX_ACCOMMODATION_BREED = 80;

export type AccommodationDecision = 'approved' | 'pending';

/** The staff-facing words for the block and its choices (all DRAFT — one list for Miles). */
export const ACCOMMODATION_COPY = {
  title: 'RRA or pending RRA',
  helper:
    "These animals are $0 on the household's form and the addendum. For Pending, the addendum says the charges below apply if the request isn't approved — this page doesn't track the decision, so send the written notice and set up the charges in AppFolio yourself if it's denied.",
  add: 'Add an animal',
  addFull: `That is the most animals this block takes (${MAX_ACCOMMODATION_ANIMALS}).`,
  remove: 'Remove',
  decisionLegend: 'Decision',
  approved: 'RRA',
  pending: 'Pending RRA',
  ifNotApprovedRent: 'If not approved: rent per month',
  ifNotApprovedDeposit: 'If not approved: deposit',
  ifNotApprovedHelper: "Defaults to this page's per-new-animal amount.",
  nameLabel: "Animal's name",
  kindLabel: 'Kind',
  kindPlaceholder: 'Choose…',
  breedLabel: 'Breed (optional)',
} as const;

/** One animal row as the block edits it. */
export interface AccommodationRow {
  /** Local React key. Never sent, never shown. */
  key: string;
  name: string;
  /** The one "what kind of animal?" choice the household forms use ('' = not chosen yet). */
  kind: AnimalKindChoice | '';
  breed: string;
  decision: AccommodationDecision;
  /**
   * Pending only: what applies if the request is not approved. `null` = follows this page's
   * per-new-animal rent / deposit (the default), so changing the page's number moves every row
   * staff have not set by hand.
   */
  ifNotApprovedRentCents: number | null;
  ifNotApprovedDepositCents: number | null;
}

/** The page's per-new-animal amounts a pending row defaults to. */
export interface AccommodationRates {
  rentPerAnimalCents: number;
  depositPerAnimalCents: number;
}

let keySeq = 0;
/** A fresh local row key. */
export function nextAccommodationKey(): string {
  keySeq += 1;
  return `rra-row-${keySeq}`;
}

/** A blank row: nothing typed, RRA (approved) chosen, pending amounts following the page. */
export function newAccommodationRow(key: string = nextAccommodationKey()): AccommodationRow {
  return {
    key,
    name: '',
    kind: '',
    breed: '',
    decision: 'approved',
    ifNotApprovedRentCents: null,
    ifNotApprovedDepositCents: null,
  };
}

/** Whether another row can be added. */
export function canAddAccommodationRow(rows: ReadonlyArray<AccommodationRow>): boolean {
  return rows.length < MAX_ACCOMMODATION_ANIMALS;
}

/** Append a blank row (a no-op at the cap). */
export function addAccommodationRow(
  rows: ReadonlyArray<AccommodationRow>,
  key: string = nextAccommodationKey(),
): AccommodationRow[] {
  if (!canAddAccommodationRow(rows)) return [...rows];
  return [...rows, newAccommodationRow(key)];
}

/** Patch one row. */
export function updateAccommodationRow(
  rows: ReadonlyArray<AccommodationRow>,
  key: string,
  patch: Partial<Omit<AccommodationRow, 'key'>>,
): AccommodationRow[] {
  return rows.map((r) => (r.key === key ? { ...r, ...patch } : r));
}

/** Drop one row. */
export function removeAccommodationRow(
  rows: ReadonlyArray<AccommodationRow>,
  key: string,
): AccommodationRow[] {
  return rows.filter((r) => r.key !== key);
}

/** What applies to a pending row if the request is not approved: its own number, else the page's. */
export function effectiveIfNotApprovedRentCents(row: AccommodationRow, rates: AccommodationRates): number {
  return row.ifNotApprovedRentCents ?? rates.rentPerAnimalCents;
}
export function effectiveIfNotApprovedDepositCents(row: AccommodationRow, rates: AccommodationRates): number {
  return row.ifNotApprovedDepositCents ?? rates.depositPerAnimalCents;
}

/** Whether the breed question is asked for this row (the shared matrix: dogs). */
export function accommodationAsksBreed(row: Pick<AccommodationRow, 'kind'>): boolean {
  return row.kind !== '' && asksAnimalField(row.kind, 'breed');
}

/** The label of the chosen kind ("Dog", "Small caged animal (…)"), '' when none. */
export function accommodationKindLabel(kind: AnimalKindChoice | ''): string {
  return ANIMAL_KIND_CHOICES.find((c) => c.value === kind)?.label ?? '';
}

/** Case- and spacing-insensitive name, so "Rex" and " rex " are one animal. */
function nameKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** The same animal twice, the way the API judges it: same name and same stored type. */
function duplicateKey(row: AccommodationRow): string {
  const type = row.kind === '' ? '' : animalTypeForChoice(row.kind).type;
  return `${nameKey(row.name)}|${type}`;
}

/** How a row is named in a sentence: its name, else its place in the list. */
function rowLabel(row: AccommodationRow, index: number): string {
  return row.name.trim() || `RRA or pending RRA animal ${index + 1}`;
}

/**
 * Why the block cannot be sent yet, in words staff can act on. Empty ⇒ ready (and an empty block is
 * ready: most addenda have no RRA animals). Each reason renders next to the (disabled) Send button.
 */
export function accommodationReasons(
  rows: ReadonlyArray<AccommodationRow>,
  rates: AccommodationRates,
): string[] {
  const out: string[] = [];
  if (rows.length > MAX_ACCOMMODATION_ANIMALS) {
    out.push(`RRA or pending RRA takes at most ${MAX_ACCOMMODATION_ANIMALS} animals.`);
  }
  const firstNameFor = new Map<string, string>();
  const reportedDuplicate = new Set<string>();
  rows.forEach((row, i) => {
    const label = rowLabel(row, i);
    const name = row.name.trim();
    if (!name) {
      out.push(`${label} needs a name.`);
    } else if (name.length > MAX_ACCOMMODATION_NAME) {
      out.push(`${label}'s name is too long (${MAX_ACCOMMODATION_NAME} characters at most).`);
    }
    if (row.kind === '') {
      out.push(`${label}: choose what kind of animal it is.`);
    }
    if (name && row.kind !== '') {
      const dup = duplicateKey(row);
      const first = firstNameFor.get(dup);
      if (first !== undefined) {
        if (!reportedDuplicate.has(dup)) {
          reportedDuplicate.add(dup);
          // The first spelling staff typed, so the reason matches what they see at the top.
          out.push(`${first} is listed twice under RRA or pending RRA — remove one.`);
        }
      } else {
        firstNameFor.set(dup, name);
      }
    }
    if (row.decision === 'pending' && effectiveIfNotApprovedRentCents(row, rates) <= 0) {
      out.push(`${label}: set the monthly rent that applies if the request isn't approved.`);
    }
  });
  return out;
}

/**
 * The wire value for `accommodationAnimals`: every row, in order, once the block is valid. An empty
 * block is `[]` (the page then omits the field). Returns null when a reason remains, so a half-filled
 * block can never be POSTed.
 *
 * Answers the kind is not asked are dropped rather than sent: a breed typed before the kind was
 * switched away from a dog goes nowhere. The pending amounts go only on pending rows.
 */
export function buildAccommodationAnimals(
  rows: ReadonlyArray<AccommodationRow>,
  rates: AccommodationRates,
): AnimalAddendumAccommodationAnimalInput[] | null {
  if (accommodationReasons(rows, rates).length > 0) return null;
  return rows.map((row) => {
    // accommodationReasons guarantees a kind, so the cast only narrows '' away.
    const stored = animalTypeForChoice(row.kind as AnimalKindChoice);
    const breed = accommodationAsksBreed(row) ? row.breed.trim().slice(0, MAX_ACCOMMODATION_BREED) : '';
    const wire: AnimalAddendumAccommodationAnimalInput = {
      name: row.name.trim(),
      type: stored.type,
      ...(stored.otherKind ? { otherKind: stored.otherKind } : {}),
      ...(breed ? { breed } : {}),
      decision: row.decision,
    };
    if (row.decision === 'pending') {
      wire.ifNotApprovedRentCents = effectiveIfNotApprovedRentCents(row, rates);
      wire.ifNotApprovedDepositCents = effectiveIfNotApprovedDepositCents(row, rates);
    }
    return wire;
  });
}

/**
 * One summary line per named row, for the money card (DRAFT):
 *   "Biscuit: no added charge"
 *   "Rex: $0 while pending — $50.00/month and a $500.00 deposit if not approved"
 */
export function accommodationSummaryLines(
  rows: ReadonlyArray<AccommodationRow>,
  rates: AccommodationRates,
): string[] {
  const out: string[] = [];
  for (const row of rows) {
    const name = row.name.trim();
    if (!name) continue;
    if (row.decision === 'pending') {
      out.push(
        `${name}: $0 while pending — ${formatMoneyCents(effectiveIfNotApprovedRentCents(row, rates))}/month and a ` +
          `${formatMoneyCents(effectiveIfNotApprovedDepositCents(row, rates))} deposit if not approved`,
      );
    } else {
      out.push(`${name}: no added charge`);
    }
  }
  return out;
}
